import { formTimeBucket, type InteractionAction, type InteractionArea } from "@bt/domain";
import { useEffect, useRef } from "react";
import type { FileRoutesById } from "../routeTree.gen";
import { garage } from "./garage";

/**
 * Interactions (ADR 0016; docs/admin-app/figures.md › Interactions): what only the browser knows
 * about using the app, counted for people who share usage statistics, on a hosted server only.
 * A fixed vocabulary of counts goes out about once a minute: never an item, a text, a URL or a
 * finer time than the batch.
 */

/** How often the counts go to the server. */
const SEND_EVERY_MS = 60_000;
/** The server takes at most this many counts in one batch. */
const MAX_COUNTS = 200;

interface Interaction {
  area: InteractionArea;
  action: InteractionAction;
}

/**
 * What opening each page counts. A new route without an entry fails `pnpm typecheck`, so every
 * page is decided on; the overview is no area of its own.
 */
const ROUTES = {
  "/": null,
  "/to-order": { area: "to_order", action: "open_list" },
  "/inventory/": { area: "parts", action: "open_list" },
  "/inventory/$partId": { area: "parts", action: "open_item" },
  "/vehicles/": { area: "vehicles", action: "open_list" },
  "/vehicles/$vehicleId": { area: "vehicles", action: "open_item" },
  "/work-orders/": { area: "work_orders", action: "open_list" },
  "/work-orders/$workOrderId": { area: "work_orders", action: "open_item" },
  "/templates/": { area: "templates", action: "open_list" },
  "/templates/$templateId": { area: "templates", action: "open_item" },
  "/orders/": { area: "orders", action: "open_list" },
  "/orders/$orderId": { area: "orders", action: "open_item" },
  "/settings/": { area: "settings", action: "open_list" },
  "/settings/packages": { area: "settings", action: "open_item" },
} as const satisfies Record<Exclude<keyof FileRoutesById, "__root__">, Interaction | null>;

/** What opening the page of `routeId` counts, if anything. */
export function routeInteraction(routeId: string): Interaction | null {
  return (ROUTES as Record<string, Interaction | null>)[routeId] ?? null;
}

let sharing = false;
let pending = new Map<string, Interaction & { formSeconds?: number; count: number }>();

/** Turned on for someone signed in who shares usage statistics; off throws away what is counted. */
export function setSharing(on: boolean): void {
  sharing = on;
  if (!on) pending = new Map();
}

/** Counts one interaction, if this person shares usage statistics. */
export function countInteraction(
  area: InteractionArea,
  action: InteractionAction,
  formSeconds?: number,
): void {
  if (!sharing || !garage) return;
  const key = `${area} ${action} ${formSeconds ?? ""}`;
  const entry = pending.get(key) ?? {
    area,
    action,
    ...(formSeconds !== undefined && { formSeconds }),
    count: 0,
  };
  entry.count++;
  pending.set(key, entry);
  if (pending.size >= MAX_COUNTS) send(false);
}

/** Sends what was counted; with `beacon` as the page goes away, which a fetch might not outlive. */
function send(beacon: boolean): void {
  if (!pending.size || !garage) return;
  const body = JSON.stringify({ counts: [...pending.values()] });
  pending = new Map();
  const url = `/api/g/${encodeURIComponent(garage)}/interactions`;
  if (beacon && navigator.sendBeacon?.(url, new Blob([body], { type: "application/json" }))) return;
  void fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

let started = false;

/** Starts sending: once a minute, and when the tab is hidden. */
export function startInteractions(): void {
  if (started) return;
  started = true;
  setInterval(() => send(false), SEND_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") send(true);
  });
}

/**
 * A form in a sheet or dialog: counts Edit or New when it opens, then saved or cancelled when it
 * closes, with how long it was open in buckets. Call the returned function when it saved.
 */
export function useFormInteraction(
  open: boolean,
  area: InteractionArea,
  editing: boolean,
): () => void {
  const openedAt = useRef<number | null>(null);
  const saved = useRef(false);
  const areaRef = useRef(area);
  areaRef.current = area;
  /** Saved or cancelled, once, when the form closes or goes away while open. */
  const closed = useRef(() => {
    if (openedAt.current === null) return;
    const seconds = (Date.now() - openedAt.current) / 1000;
    openedAt.current = null;
    countInteraction(areaRef.current, saved.current ? "save" : "cancel", formTimeBucket(seconds));
  });
  useEffect(() => {
    if (open && openedAt.current === null) {
      openedAt.current = Date.now();
      saved.current = false;
      countInteraction(area, editing ? "open_edit" : "open_create");
    } else if (!open) {
      closed.current();
    }
  }, [open, area, editing]);
  // Saving a new record can open its page, which takes the form away before it closes.
  useEffect(() => () => closed.current(), []);
  return () => {
    saved.current = true;
  };
}
